import React, { useState } from "react";

import { IConfirmModalProps } from "@amphore-dev/amphore-lib";

import { ArchiveConfirmModal } from "@/components/molecules/ArchiveConfirmModal/ArchiveConfirmModal";
import { DeleteTasksConfirmModal } from "@/components/molecules/DeleteTasksConfirmModal/DeleteTasksConfirmModal";
import { IdleModal } from "@/components/organisms/IdleModal/IdleModal";
import { UnsavedChangesModal } from "@/components/organisms/UnsavedChangesModal/UnsavedChangesModal";

import { TTask } from "@/types";

import { ModalContext, TModalState } from "./context";

const MODALS = {
	idle: () => <IdleModal />,
	unsavedChanges: (
		props: Omit<IConfirmModalProps, "open" | "onClose">,
		onClose: () => void
	) => <UnsavedChangesModal {...props} open onClose={onClose} />,
	deleteTasksConfirm: (
		props: { onConfirm: () => void; items: TTask[] },
		onClose: () => void
	) => <DeleteTasksConfirmModal {...props} open onClose={onClose} />,
	archiveConfirm: (
		props: {
			onConfirm: () => void;
			kind: "project" | "view";
			name: string;
		},
		onClose: () => void
	) => <ArchiveConfirmModal {...props} open onClose={onClose} />,
};

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [modal, setModal] = useState<TModalState | null>(null);

	const closeModal = () => setModal(null);

	const renderModal = () => {
		if (!modal) return null;
		const render = MODALS[modal.type] as (
			props: never,
			onClose: () => void
		) => React.ReactNode;
		return render(modal.props as never, closeModal);
	};

	return (
		<ModalContext.Provider
			value={{
				modal,
				openModal: setModal,
				closeModal,
			}}
		>
			{children}
			{modal && (
				<React.Suspense fallback={null}>{renderModal()}</React.Suspense>
			)}
		</ModalContext.Provider>
	);
};
